"use client";

import { c as _c } from "react/compiler-runtime";
import { motion, useSpring } from "motion/react";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { chartCssVars, useChart } from "../chart-context";
import { DateTicker } from "./date-ticker";
import { TooltipBox } from "./tooltip-box";
import { TooltipContent, type TooltipRow } from "./tooltip-content";
import { TooltipDot } from "./tooltip-dot";
import { TooltipIndicator } from "./tooltip-indicator";

// Spring config for crosshair
const crosshairSpringConfig = {
  stiffness: 300,
  damping: 30
};
export interface ChartTooltipProps {
  /** Whether to show the date pill at bottom. Default: true */
  showDatePill?: boolean;
  /** Whether to show the vertical crosshair line. Default: true */
  showCrosshair?: boolean;
  /** Whether to show dots on the lines. Default: true */
  showDots?: boolean;
  /** Custom content renderer for the tooltip box */
  content?: (props: {
    point: Record<string, unknown>;
    index: number;
  }) => React.ReactNode;
  /** Custom row renderer - return array of TooltipRow */
  rows?: (point: Record<string, unknown>) => TooltipRow[];
  /** Additional content to show below rows (e.g., markers) */
  children?: React.ReactNode;
  /** Custom class name */
  className?: string;
}
export function ChartTooltip(t0) {
  const $ = _c(68);
  const {
    showDatePill: t1,
    showCrosshair: t2,
    showDots: t3,
    content,
    rows: rowsRenderer,
    children,
    className: t4
  } = t0;
  const showDatePill = t1 === undefined ? true : t1;
  const showCrosshair = t2 === undefined ? true : t2;
  const showDots = t3 === undefined ? true : t3;
  const className = t4 === undefined ? "" : t4;
  const {
    tooltipData,
    width,
    height,
    innerHeight,
    margin,
    columnWidth,
    lines,
    xAccessor,
    dateLabels,
    containerRef,
    orientation,
    barXAccessor
  } = useChart();
  const isHorizontal = orientation === "horizontal";
  const [portalContainer, setPortalContainer] = useState(null);
  if (containerRef.current && containerRef.current !== portalContainer) {
    setPortalContainer(containerRef.current);
  }
  const visible = tooltipData !== null;
  const x = tooltipData?.x ?? 0;
  const xWithMargin = x + margin.left;
  const firstLineDataKey = lines[0]?.dataKey;
  const firstLineY = firstLineDataKey ? tooltipData?.yPositions[firstLineDataKey] ?? 0 : 0;
  const yWithMargin = firstLineY + margin.top;
  const animatedX = useSpring(xWithMargin, crosshairSpringConfig);
  let t5;
  let t6;
  if ($[0] !== animatedX || $[1] !== xWithMargin) {
    t5 = () => {
      animatedX.set(xWithMargin);
    };
    t6 = [xWithMargin, animatedX];
    $[0] = animatedX;
    $[1] = xWithMargin;
    $[2] = t5;
    $[3] = t6;
  } else {
    t5 = $[2];
    t6 = $[3];
  }
  useEffect(t5, t6);
  let t7;
  bb0: {
    if (!tooltipData) {
      let t8;
      if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = [];
        $[4] = t8;
      } else {
        t8 = $[4];
      }
      t7 = t8;
      break bb0;
    }
    if (rowsRenderer) {
      let t8;
      if ($[5] !== rowsRenderer || $[6] !== tooltipData.point) {
        t8 = rowsRenderer(tooltipData.point);
        $[5] = rowsRenderer;
        $[6] = tooltipData.point;
        $[7] = t8;
      } else {
        t8 = $[7];
      }
      t7 = t8;
      break bb0;
    }
    let t8;
    if ($[8] !== lines || $[9] !== tooltipData) {
      let t9;
      if ($[11] !== tooltipData) {
        t9 = line => ({
          color: line.stroke,
          label: line.dataKey,
          value: tooltipData.point[line.dataKey] as number ?? 0
        });
        $[11] = tooltipData;
        $[12] = t9;
      } else {
        t9 = $[12];
      }
      t8 = lines.map(t9);
      $[8] = lines;
      $[9] = tooltipData;
      $[10] = t8;
    } else {
      t8 = $[10];
    }
    t7 = t8;
  }
  const tooltipRows = t7;
  let t8;
  bb1: {
    if (!tooltipData) {
      t8 = undefined;
      break bb1;
    }
    if (barXAccessor) {
      const tooltipLabel = tooltipData.point.tooltipLabel;
      if (typeof tooltipLabel === "string" && tooltipLabel.length > 0) {
        t8 = tooltipLabel;
        break bb1;
      }
      let t9;
      if ($[13] !== barXAccessor || $[14] !== tooltipData.point) {
        t9 = barXAccessor(tooltipData.point);
        $[13] = barXAccessor;
        $[14] = tooltipData.point;
        $[15] = t9;
      } else {
        t9 = $[15];
      }
      t8 = t9;
      break bb1;
    }
    let t9;
    if ($[16] !== tooltipData.point || $[17] !== xAccessor) {
      t9 = xAccessor(tooltipData.point).toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric"
      });
      $[16] = tooltipData.point;
      $[17] = xAccessor;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    t8 = t9;
  }
  const title = t8;
  if (!portalContainer) {
    return null;
  }
  let t9;
  if ($[19] !== columnWidth || $[20] !== innerHeight || $[21] !== margin.left || $[22] !== margin.top || $[23] !== showCrosshair || $[24] !== visible || $[25] !== x) {
    t9 = showCrosshair && <svg aria-hidden="true" className="pointer-events-none absolute inset-0" height="100%" width="100%"><g transform={`translate(${margin.left},${margin.top})`}><TooltipIndicator colorEdge={chartCssVars.crosshair} colorMid={chartCssVars.crosshair} columnWidth={columnWidth} fadeEdges={true} height={innerHeight} visible={visible} width="line" x={x} /></g></svg>;
    $[19] = columnWidth;
    $[20] = innerHeight;
    $[21] = margin.left;
    $[22] = margin.top;
    $[23] = showCrosshair;
    $[24] = visible;
    $[25] = x;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== isHorizontal || $[28] !== lines || $[29] !== margin.left || $[30] !== margin.top || $[31] !== showDots || $[32] !== tooltipData?.xPositions || $[33] !== tooltipData?.yPositions || $[34] !== visible || $[35] !== x) {
    t10 = showDots && visible && !isHorizontal && <svg aria-hidden="true" className="pointer-events-none absolute inset-0" height="100%" width="100%"><g transform={`translate(${margin.left},${margin.top})`}>{lines.map(line_0 => <TooltipDot color={line_0.stroke} key={line_0.dataKey} strokeColor={chartCssVars.background} visible={visible} x={tooltipData?.xPositions?.[line_0.dataKey] ?? x} y={tooltipData?.yPositions[line_0.dataKey] ?? 0} />)}</g></svg>;
    $[27] = isHorizontal;
    $[28] = lines;
    $[29] = margin.left;
    $[30] = margin.top;
    $[31] = showDots;
    $[32] = tooltipData?.xPositions;
    $[33] = tooltipData?.yPositions;
    $[34] = visible;
    $[35] = x;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  const t11 = isHorizontal ? undefined : margin.top;
  const t12 = isHorizontal ? yWithMargin : margin.top;
  let t13;
  if ($[37] !== children || $[38] !== content || $[39] !== title || $[40] !== tooltipData || $[41] !== tooltipRows) {
    t13 = content && tooltipData ? content({
      point: tooltipData.point,
      index: tooltipData.index
    }) : !content && <TooltipContent rows={tooltipRows} title={title}>{children}</TooltipContent>;
    $[37] = children;
    $[38] = content;
    $[39] = title;
    $[40] = tooltipData;
    $[41] = tooltipRows;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== className || $[44] !== containerRef || $[45] !== height || $[46] !== t11 || $[47] !== t12 || $[48] !== t13 || $[49] !== visible || $[50] !== width || $[51] !== xWithMargin) {
    t14 = <TooltipBox className={className} containerHeight={height} containerRef={containerRef} containerWidth={width} top={t11} visible={visible} x={xWithMargin} y={t12}>{t13}</TooltipBox>;
    $[43] = className;
    $[44] = containerRef;
    $[45] = height;
    $[46] = t11;
    $[47] = t12;
    $[48] = t13;
    $[49] = visible;
    $[50] = width;
    $[51] = xWithMargin;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  let t15;
  if ($[53] !== animatedX || $[54] !== dateLabels || $[55] !== isHorizontal || $[56] !== showDatePill || $[57] !== tooltipData?.index || $[58] !== visible) {
    t15 = showDatePill && dateLabels.length > 0 && visible && !isHorizontal && <motion.div className="pointer-events-none absolute z-50" style={{
      left: animatedX,
      transform: "translateX(-50%)",
      bottom: 4
    }}><DateTicker currentIndex={tooltipData?.index ?? 0} labels={dateLabels} visible={visible} /></motion.div>;
    $[53] = animatedX;
    $[54] = dateLabels;
    $[55] = isHorizontal;
    $[56] = showDatePill;
    $[57] = tooltipData?.index;
    $[58] = visible;
    $[59] = t15;
  } else {
    t15 = $[59];
  }
  let t16;
  if ($[60] !== t10 || $[61] !== t14 || $[62] !== t15 || $[63] !== t9) {
    t16 = <>{t9}{t10}{t14}{t15}</>;
    $[60] = t10;
    $[61] = t14;
    $[62] = t15;
    $[63] = t9;
    $[64] = t16;
  } else {
    t16 = $[64];
  }
  const tooltipContent = t16;
  let t17;
  if ($[65] !== portalContainer || $[66] !== tooltipContent) {
    t17 = createPortal(tooltipContent, portalContainer);
    $[65] = portalContainer;
    $[66] = tooltipContent;
    $[67] = t17;
  } else {
    t17 = $[67];
  }
  return t17;
}
ChartTooltip.displayName = "ChartTooltip";
export default ChartTooltip;